import React, { useMemo } from 'react';
import { MapPin, Phone, StickyNote, Trash2 } from 'lucide-react';

const ROW_HEIGHT = 72;
const VISIBLE_HOURS = 8;
const MIN_BLOCK_HEIGHT = 42;

const ADMIN_RESIDENTIAL_TIME_SLOTS = [
  '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM',
];

const ADMIN_COMMERCIAL_TIME_SLOTS = ['8:00 AM', ...ADMIN_RESIDENTIAL_TIME_SLOTS];

const TRACK_CONFIG = {
  residential: {
    title: 'Platinum schedule',
    empty: 'No Platinum bookings yet for this day.',
    startMinute: 9 * 60,
    endMinute: 18 * 60,
    slotTimes: ADMIN_RESIDENTIAL_TIME_SLOTS,
    lanes: [
      { id: 'crew_a', label: 'Crew A', headerClass: 'bg-blue-50 text-blue-700 border-blue-200' },
      {
        id: 'crew_b',
        label: 'Crew B (Disabled)',
        headerClass: 'bg-slate-100 text-slate-500 border-slate-200',
        disabled: true,
      },
    ],
  },
  full_detailing: {
    title: 'Full Detailing schedule',
    empty: 'No Full Detailing bookings yet for this day.',
    startMinute: 9 * 60,
    endMinute: 17 * 60,
    slotTimes: ['9:00 AM', '11:00 AM', '1:00 PM', '3:00 PM'],
    lanes: [
      { id: 'detailing', label: 'Detailing Crew', headerClass: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
      {
        id: 'detailing-secondary',
        label: 'No second crew',
        headerClass: 'bg-slate-100 text-slate-500 border-slate-200',
        disabled: true,
      },
    ],
  },
  commercial: {
    title: 'Commercial schedule',
    empty: 'No Commercial bookings yet for this day.',
    startMinute: 8 * 60,
    endMinute: 18 * 60,
    slotTimes: ADMIN_COMMERCIAL_TIME_SLOTS,
    lanes: [
      { id: 'crew_a', label: 'Crew A', headerClass: 'bg-blue-50 text-blue-700 border-blue-200' },
      { id: 'crew_b', label: 'Crew B', headerClass: 'bg-orange-50 text-orange-700 border-orange-200' },
    ],
  },
};

function parseTimeToMinutes(timeString) {
  if (!timeString) return 0;
  const match = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return 0;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const period = match[3].toUpperCase();

  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  return hours * 60 + minutes;
}

function formatMinutes(minutes) {
  const hours24 = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const suffix = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${String(mins).padStart(2, '0')} ${suffix}`;
}

function getScheduleTrack(item = {}) {
  if (item.service_type === 'commercial' || item.service_category === 'commercial') return 'commercial';
  if (item.service_type === 'full_detailing') return 'full_detailing';
  return 'residential';
}

function getBookingDurationMinutes(item = {}) {
  const totalVehicles = Number(item.total_vehicles || item.vehicle_count || 1) || 1;
  return item.service_type === 'full_detailing' ? totalVehicles * 120 : totalVehicles * 60;
}

function getBookingTheme(item = {}, serviceType) {
  if (item.corporate_id) {
    return {
      blockClass: 'bg-amber-50 border-amber-300 text-amber-950 shadow-amber-100/60',
      accentClass: 'bg-amber-500',
      badge: 'Corporate ID',
      badgeClass: 'bg-amber-100 text-amber-800 border border-amber-300',
    };
  }

  if (serviceType === 'commercial') {
    return {
      blockClass: 'bg-emerald-50 border-emerald-300 text-emerald-950 shadow-emerald-100/60',
      accentClass: 'bg-emerald-500',
      badge: 'Commercial',
      badgeClass: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
    };
  }

  if (serviceType === 'full_detailing') {
    return {
      blockClass: 'bg-cyan-50 border-cyan-300 text-cyan-950 shadow-cyan-100/60',
      accentClass: 'bg-cyan-500',
      badge: 'Detailing',
      badgeClass: 'bg-cyan-100 text-cyan-800 border border-cyan-300',
    };
  }

  return {
    blockClass: 'bg-blue-50 border-blue-300 text-blue-950 shadow-blue-100/60',
    accentClass: 'bg-blue-500',
    badge: 'Platinum',
    badgeClass: 'bg-blue-100 text-blue-800 border border-blue-300',
  };
}

function getLaneIdsForBooking(item, serviceType, laneUsageBySlot) {
  if (serviceType === 'full_detailing') return ['detailing'];

  const slotKey = item.preferred_time || `${item.id || 'booking'}-${serviceType}`;
  const usage = laneUsageBySlot[slotKey] || { crew_a: 0, crew_b: 0 };
  laneUsageBySlot[slotKey] = usage;

  if (item.crew_assignment === 'both') {
    usage.crew_a += 1;
    usage.crew_b += 1;
    return ['crew_a', 'crew_b'];
  }

  if (item.crew_assignment === 'crew_a' || item.crew_assignment === 'crew_b') {
    usage[item.crew_assignment] += 1;
    return [item.crew_assignment];
  }

  if (serviceType === 'commercial') {
    usage.crew_a += 1;
    usage.crew_b += 1;
    return ['crew_a', 'crew_b'];
  }

  const nextLane = usage.crew_a <= usage.crew_b ? 'crew_a' : 'crew_b';
  usage[nextLane] += 1;
  return [nextLane];
}

function getBlockLaneIds(item, serviceType) {
  if (serviceType === 'full_detailing') return item.crew_target === 'detailing' ? ['detailing'] : [];
  if (item.crew_target === 'both' || item.crew_target === 'all') return ['crew_a', 'crew_b'];
  if (item.crew_target === 'crew_a' || item.crew_target === 'crew_b') return [item.crew_target];
  return [];
}

function buildUnavailableRanges(slotAvailability, slotTimes, endMinute) {
  if (!slotAvailability) return [];

  return slotTimes
    .map((slot, index) => {
      if (slotAvailability[slot] !== false) return null;
      const startMinute = parseTimeToMinutes(slot);
      const nextSlotMinute = slotTimes[index + 1] ? parseTimeToMinutes(slotTimes[index + 1]) : Math.min(endMinute, startMinute + 60);
      return {
        id: `unavailable-${slot}`,
        startMinute,
        endMinute: Math.max(startMinute + 30, nextSlotMinute),
      };
    })
    .filter(Boolean);
}

function buildHourRows(startMinute, endMinute) {
  const rows = [];
  for (let minute = startMinute; minute < endMinute; minute += 60) {
    rows.push({
      minute,
      top: ((minute - startMinute) / 60) * ROW_HEIGHT,
    });
  }
  return rows;
}

function buildEntryMeta(entry, startMinute, endMinute) {
  const clampedStart = Math.max(entry.startMinute, startMinute);
  const clampedEnd = Math.min(entry.endMinute, endMinute);

  return {
    ...entry,
    top: ((clampedStart - startMinute) / 60) * ROW_HEIGHT,
    height: Math.max(MIN_BLOCK_HEIGHT, ((clampedEnd - clampedStart) / 60) * ROW_HEIGHT - 8),
  };
}

export const TrackSchedulePreview = ({
  date,
  serviceType,
  scheduleData,
  slotAvailability,
  onDeleteBlock,
  onEditBooking,
  showAvailabilityHint = false,
}) => {
  const config = TRACK_CONFIG[serviceType] || TRACK_CONFIG.residential;
  const hourRows = useMemo(() => buildHourRows(config.startMinute, config.endMinute), [config.endMinute, config.startMinute]);
  const totalHeight = hourRows.length * ROW_HEIGHT;
  const viewportHeight = Math.min(totalHeight || ROW_HEIGHT * VISIBLE_HOURS, ROW_HEIGHT * VISIBLE_HOURS);

  const entries = useMemo(() => {
    const laneUsageBySlot = {};
    const bookings = (scheduleData?.bookings || [])
      .filter((item) => item.status !== 'cancelled' && getScheduleTrack(item) === serviceType)
      .sort((a, b) => parseTimeToMinutes(a.preferred_time) - parseTimeToMinutes(b.preferred_time));

    const bookingEntries = bookings.flatMap((item) => {
      const startMinute = parseTimeToMinutes(item.preferred_time);
      const endMinute = item.end_time ? parseTimeToMinutes(item.end_time) : startMinute + getBookingDurationMinutes(item);
      const laneIds = getLaneIdsForBooking(item, serviceType, laneUsageBySlot);
      const theme = getBookingTheme(item, serviceType);
      const endLabel = formatMinutes(Math.max(endMinute, startMinute + 30));

      return laneIds.map((laneId) => ({
        id: `${item.id}-${laneId}`,
        baseId: item.id,
        rawBooking: item,
        laneId,
        startMinute,
        endMinute: Math.max(endMinute, startMinute + 30),
        title: item.customer?.full_name || item.service_name || 'Booking',
        timeLabel: `${item.preferred_time} - ${endLabel}`,
        serviceLabel: item.service_name || 'Booking',
        phone: item.customer?.phone || '',
        address: item.service_address ? `${item.service_address.street || ''}, ${item.service_address.city || ''}`.replace(/^,\s*/, '') : '',
        notes: item.notes || '',
        badge: laneIds.length > 1 ? 'Both crews' : theme.badge,
        badgeClass: laneIds.length > 1 ? 'bg-slate-100 text-slate-700 border border-slate-300' : theme.badgeClass,
        blockClass: theme.blockClass,
        accentClass: theme.accentClass,
        isBlock: false,
      }));
    });

    const relevantBlocks = (scheduleData?.time_blocks || []).filter((item) => {
      if (serviceType === 'commercial') return item.crew_target !== 'detailing';
      if (serviceType === 'full_detailing') return item.crew_target === 'detailing';
      return item.block_type === 'admin_block' && ['crew_a', 'crew_b', 'both', 'all'].includes(item.crew_target);
    });

    const blockEntries = relevantBlocks.flatMap((item) => {
      const laneIds = getBlockLaneIds(item, serviceType);
      const startMinute = parseTimeToMinutes(item.start_time);
      const endMinute = parseTimeToMinutes(item.end_time) || startMinute + 60;

      return laneIds.map((laneId) => ({
        id: `${item.id}-${laneId}`,
        baseId: item.id,
        laneId,
        startMinute,
        endMinute: Math.max(endMinute, startMinute + 30),
        title: item.title || 'Time block',
        timeLabel: `${item.start_time} - ${item.end_time}`,
        serviceLabel: item.block_type === 'commercial' ? 'Commercial block' : 'Admin block',
        phone: '',
        address: '',
        notes: item.notes || '',
        badge: laneIds.length > 1 ? 'Both crews' : 'Time block',
        badgeClass: 'bg-slate-100 text-slate-700 border border-slate-300',
        blockClass: 'bg-slate-100 border-slate-300 text-slate-900 shadow-slate-200/70',
        accentClass: laneId === 'crew_a' ? 'bg-blue-500' : laneId === 'crew_b' ? 'bg-orange-500' : 'bg-cyan-500',
        isBlock: true,
      }));
    });

    return [...bookingEntries, ...blockEntries]
      .filter((entry) => entry.endMinute > config.startMinute && entry.startMinute < config.endMinute)
      .map((entry) => buildEntryMeta(entry, config.startMinute, config.endMinute));
  }, [config.endMinute, config.startMinute, scheduleData, serviceType]);

  const unavailableRanges = useMemo(
    () => buildUnavailableRanges(slotAvailability, config.slotTimes, config.endMinute).map((range) => ({
      ...range,
      top: ((Math.max(range.startMinute, config.startMinute) - config.startMinute) / 60) * ROW_HEIGHT,
      height: Math.max(18, ((Math.min(range.endMinute, config.endMinute) - Math.max(range.startMinute, config.startMinute)) / 60) * ROW_HEIGHT),
    })),
    [config.endMinute, config.slotTimes, config.startMinute, slotAvailability]
  );

  const hasVisibleContent = entries.length > 0 || unavailableRanges.length > 0;

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={`track-schedule-preview-${serviceType}`}>
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-900">{config.title}</h4>
            <p className="text-xs text-slate-500">{date ? `Eight-hour day view for ${date}` : 'Select a date to inspect this schedule.'}</p>
          </div>
          {showAvailabilityHint && (
            <span
              className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-600"
              data-testid={`track-availability-hint-${serviceType}`}
            >
              Gray ranges are fully booked
            </span>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-[84px_minmax(0,1fr)_minmax(0,1fr)] border-b border-slate-200 bg-white">
            <div className="border-r border-slate-200 bg-slate-50 px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Time
            </div>
            {config.lanes.map((lane, index) => (
              <div
                key={`${serviceType}-${lane.id}-header`}
                className={`px-3 py-3 text-center text-xs font-semibold ${lane.headerClass} ${index === 0 ? 'border-r border-slate-200' : ''}`}
                data-testid={`track-lane-header-${serviceType}-${lane.id}`}
              >
                {lane.label}
              </div>
            ))}
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: `${viewportHeight}px` }}>
            <div className="grid grid-cols-[84px_minmax(0,1fr)_minmax(0,1fr)]" style={{ minHeight: `${totalHeight}px` }}>
              <div className="relative border-r border-slate-200 bg-slate-50" style={{ height: `${totalHeight}px` }}>
                {hourRows.map((row) => (
                  <div key={`${serviceType}-time-row-${row.minute}`}>
                    <div
                      className="absolute inset-x-0 border-t border-slate-200"
                      style={{ top: `${row.top}px` }}
                    />
                    <div
                      className="absolute inset-x-0 px-2 pt-2 text-right text-[11px] font-semibold text-slate-600"
                      style={{ top: `${row.top}px` }}
                      data-testid={`track-hour-label-${serviceType}-${row.minute}`}
                    >
                      {formatMinutes(row.minute)}
                    </div>
                  </div>
                ))}
                <div className="absolute inset-x-0 bottom-0 border-t border-slate-200" />
              </div>

              {config.lanes.map((lane, index) => {
                const laneEntries = entries.filter((entry) => entry.laneId === lane.id);

                return (
                  <div
                    key={`${serviceType}-${lane.id}`}
                    className={`relative bg-white ${index === 0 ? 'border-r border-slate-200' : ''}`}
                    style={{ height: `${totalHeight}px` }}
                    data-testid={`track-lane-${serviceType}-${lane.id}`}
                  >
                    {hourRows.map((row) => (
                      <div key={`${serviceType}-${lane.id}-${row.minute}`}>
                        <div
                          className="absolute inset-x-0 border-t border-slate-200"
                          style={{ top: `${row.top}px` }}
                        />
                        <div
                          className="absolute inset-x-0 border-t border-dashed border-slate-100"
                          style={{ top: `${row.top + ROW_HEIGHT / 2}px` }}
                        />
                      </div>
                    ))}
                    <div className="absolute inset-x-0 bottom-0 border-t border-slate-200" />

                    {lane.disabled && (
                      <div className="absolute inset-0 bg-slate-100/60">
                        <div className="absolute inset-x-6 top-6 rounded-xl border border-dashed border-slate-300 bg-white/75 px-4 py-3 text-center text-xs font-medium text-slate-500">
                          {serviceType === 'full_detailing' 
                            ? 'Full Detailing uses one dedicated crew, so this side stays empty.'
                            : 'This crew track is currently disabled.'}
                        </div>
                      </div>
                    )}

                    {!lane.disabled && unavailableRanges.map((range) => (
                      <div
                        key={`${serviceType}-${lane.id}-${range.id}`}
                        className="absolute inset-x-0 bg-slate-200/55"
                        style={{ top: `${range.top}px`, height: `${range.height}px` }}
                      />
                    ))}

                    {!lane.disabled && laneEntries.map((entry) => (
                      <article
                        key={entry.id}
                        className={`absolute left-2 right-2 overflow-hidden rounded-xl border p-3 shadow-sm ${entry.blockClass} ${!entry.isBlock && onEditBooking ? 'cursor-pointer hover:ring-2 hover:ring-slate-400 transition-all' : ''}`}
                        style={{ top: `${entry.top}px`, height: `${entry.height}px` }}
                        data-testid={`track-entry-${serviceType}-${entry.baseId}-${lane.id}`}
                        onClick={() => {
                          if (!entry.isBlock && onEditBooking) onEditBooking(entry.rawBooking);
                        }}
                      >
                        <div className={`absolute inset-y-2 left-0.5 w-1 rounded-full ${entry.accentClass}`} />
                        <div className="ml-2 space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-[11px] font-bold leading-4 text-slate-900">{entry.title}</p>
                              <p className="text-[11px] leading-4 text-slate-600">{entry.timeLabel}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${entry.badgeClass}`}>
                                {entry.badge}
                              </span>
                              {entry.isBlock && onDeleteBlock && (
                                <button
                                  type="button"
                                  onClick={() => onDeleteBlock(entry.baseId)}
                                  className="rounded-full p-1 text-slate-500 transition hover:bg-white/70 hover:text-red-600"
                                  data-testid={`track-delete-block-${serviceType}-${entry.baseId}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </div>

                          <p className="text-[11px] font-medium leading-4 text-slate-700">{entry.serviceLabel}</p>

                          {entry.phone && (
                            <p className="flex items-center gap-1 text-[11px] leading-4 text-slate-600">
                              <Phone className="h-3 w-3 flex-none" />
                              <span className="truncate">{entry.phone}</span>
                            </p>
                          )}

                          {entry.address && (
                            <p className="flex items-center gap-1 text-[11px] leading-4 text-slate-600">
                              <MapPin className="h-3 w-3 flex-none" />
                              <span className="truncate">{entry.address}</span>
                            </p>
                          )}

                          {entry.notes && (
                            <p className="flex items-start gap-1 text-[11px] leading-4 text-slate-600">
                              <StickyNote className="mt-0.5 h-3 w-3 flex-none" />
                              <span className="line-clamp-2">{entry.notes}</span>
                            </p>
                          )}
                        </div>
                      </article>
                    ))}

                    {!lane.disabled && !hasVisibleContent && index === 0 && (
                      <div className="absolute inset-x-6 top-6 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-center text-xs font-medium text-slate-500" data-testid={`track-schedule-empty-${serviceType}`}>
                        {config.empty}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};